import { FormEvent, useEffect, useState } from "react";
import { AtSign, BadgeCheck, Eye, EyeOff, Lock, Mail, Phone } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, getFieldErrors } from "../../utils/apiError";
import { CREATABLE_ROLES } from "../../constants/enums";
import { Role, User } from "../../types";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import { Checkbox, Input, Select } from "../../components/ui/FormField";

interface Props {
    open: boolean;
    user: User | null; // null = create
    onClose: () => void;
    onSaved: () => void;
}

interface FormState {
    name: string;
    username: string;
    email: string;
    phone: string;
    password: string;
    role: Role | "";
    isActive: boolean;
}

const EMPTY: FormState = { name: "", username: "", email: "", phone: "", password: "", role: "RESELLER", isActive: true };

export default function UserFormModal({ open, user, onClose, onSaved }: Props) {
    const toast = useToast();
    const { user: me, refreshUser } = useAuth();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const isEdit = !!user;
    const isSuperadmin = user?.role === "SUPERADMIN";
    const isSelf = !!user && user.id === me?.id;

    // Why the role can't be changed (if it can't)
    const roleLock = !user
        ? ""
        : isSuperadmin
          ? "A super admin's role can't be changed."
          : isSelf
            ? "You can't change your own role."
            : user.customer
              ? `Portal login for customer ${user.customer.name}; must stay User.`
              : user.role === "RESELLER" && (user._count?.customers ?? 0) > 0
                ? `Owns ${user._count!.customers} customer(s); must stay Reseller.`
                : "";

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setShowPassword(false);
        setForm(
            user
                ? {
                      name: user.name,
                      username: user.username,
                      email: user.email,
                      phone: user.phone ?? "",
                      password: "",
                      role: user.role,
                      isActive: user.isActive ?? true,
                  }
                : EMPTY
        );
    }, [open, user]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: "" }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const body: Record<string, unknown> = {
            name: form.name.trim(),
            username: form.username.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
        };

        if (!roleLock && form.role !== user?.role) body.role = form.role;
        if (form.password) body.password = form.password;
        if (!isEdit) body.isActive = form.isActive;

        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/users/${user!.id}`, body);
                toast.success("User updated");
                if (isSelf) refreshUser().catch(() => undefined);
            } else {
                await api.post("/users", body);
                toast.success(`${form.name} can now sign in as ${form.username}`);
            }
            onSaved();
        } catch (err) {
            setErrors(getFieldErrors(err));
            toast.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const roleOptions = isSuperadmin ? [{ value: "SUPERADMIN", label: "Super admin" }] : CREATABLE_ROLES;

    return (
        <Modal
            open={open}
            onClose={onClose}
            locked={saving}
            title={isEdit ? `Edit ${user?.name}` : "Add user"}
            subtitle={isEdit ? `@${user?.username}` : "Create a reseller, developer or customer login."}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" form="user-form" loading={saving}>
                        {isEdit ? "Save changes" : "Create user"}
                    </Button>
                </>
            }
        >
            <form id="user-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                    wrapperClassName="sm:col-span-2"
                    label="Role"
                    required
                    value={form.role}
                    onChange={(e) => set("role", e.target.value as Role)}
                    options={roleOptions}
                    disabled={!!roleLock}
                    error={errors.role}
                    hint={
                        roleLock ||
                        (form.role === "RESELLER"
                            ? "Can add customers and assign products to their own customers."
                            : form.role === "USER"
                              ? "For customer portal logins, prefer creating them from the customer form so they're linked."
                              : form.role === "DEVELOPER"
                                ? "Can sign in and view their profile."
                                : undefined)
                    }
                />
                <Input label="Full name" required autoFocus icon={BadgeCheck} value={form.name} onChange={(e) => set("name", e.target.value)} error={errors.name} placeholder="John Doe" />
                <Input
                    label="Username"
                    required
                    icon={AtSign}
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => set("username", e.target.value)}
                    error={errors.username}
                    placeholder="johndoe"
                    hint="Letters, numbers, . _ -"
                />
                <Input label="Email" required type="email" icon={Mail} value={form.email} onChange={(e) => set("email", e.target.value)} error={errors.email} placeholder="john@example.com" />
                <Input label="Phone" type="tel" icon={Phone} value={form.phone} onChange={(e) => set("phone", e.target.value)} error={errors.phone} placeholder="+966…" />

                {!isSuperadmin && (
                    <Input
                        wrapperClassName="sm:col-span-2"
                        label={isEdit ? "New password" : "Password"}
                        required={!isEdit}
                        minLength={8}
                        icon={Lock}
                        autoComplete="new-password"
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => set("password", e.target.value)}
                        error={errors.password}
                        placeholder={isEdit ? "Leave blank to keep current password" : "At least 8 characters"}
                        hint={isEdit ? "Only fill this in to reset the user's password." : "Share it with the user securely."}
                        rightSlot={
                            <button type="button" onClick={() => setShowPassword((s) => !s)} className="p-1 text-slate-500 hover:text-white" aria-label="Toggle password">
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        }
                    />
                )}

                {!isEdit && (
                    <div className="sm:col-span-2">
                        <Checkbox
                            label="Active"
                            description="Inactive users can't sign in until you activate them."
                            checked={form.isActive}
                            onChange={(v) => set("isActive", v)}
                        />
                    </div>
                )}
            </form>
        </Modal>
    );
}